import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { formatSceneNarration } from '../html-design/sceneNarration';
import { MediaRegistry } from '../registries/MediaRegistry';
import { captureSceneScreenshot, SCREENSHOT_MAX_EDGE } from '../rendering/sceneScreenshot';
import {
    type EnhancedToolResult,
    type ImageContent,
    Model,
    ReasoningEffort,
    type TextContent,
    type ToolExecutionContext,
} from '../types';

import { ConversationTool } from './ConversationTool';
import { reviewSkippedResult } from './review-skipping';
import { resolveSceneById, sceneDisplayId } from './scene-addressing';

import { AssetContainerTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import {
    announceCreationStage,
    CreationStage,
    isReviewSkipped,
    setSceneReviewing,
} from 'js/src/data/agent-activity';
import { INVISIBLE_IMAGE_SOURCE_DATA } from 'js/src/libs/consts';
import { getVideoSizeAnalyticsProperties } from 'js/src/luminary/luminary-utils';

export const imageAskSchema = z.object({
    media_ids: z
        .array(z.string())
        .optional()
        .describe('IDs of media from the media registry to include as images.'),
    scene_ids: z.array(z.string()).optional(),
    question: z
        .string()
        .describe(
            'What you want to know about the image(s), specific enough to answer without seeing them. Refer to a scene by its scene_id (e.g. "scene a1b2c3") to match the image labels.',
        ),
});
export type ImageAskArgs = z.infer<typeof imageAskSchema>;

type ResolvedImage = { url: string } | { error: string };
type ResolvedScene = { sceneId: string; sceneIndex: number };

/**
 * Returns a text answer only, never the frames: the judgement is meant to come from a model with no
 * stake in the work, so there is deliberately no mode that hands them back to the caller.
 */
export class ImageAskTool extends ConversationTool<typeof imageAskSchema> {
    public override canRunAsync = true;
    public override runsAfterEdits = true;
    protected override readonly appendsVideoDiff = false;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    public override readonly childReasoningEffort = ReasoningEffort.LOW;

    protected override readonly schema = imageAskSchema;
    protected override readonly toolName = TOOL_NAMES.imageAsk;
    protected override readonly toolDescription =
        'Ask a vision model a question about scenes and/or media and get back a text answer. Use it to ' +
        'diagnose a defect you already know about — what a piece of text is sitting on, whether a ' +
        `reframe landed. To review a scene for defects, use ${TOOL_NAMES.reviewSceneImages} instead. ` +
        'The images never enter your own context, so the question has to carry what you need to know.';

    public override getToolDisplayName(): string {
        return 'Analyzing image';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(
        args: ImageAskArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (isReviewSkipped(this.getState())) {
            return { result: reviewSkippedResult(), isSuccess: true };
        }

        // Announced up front, not off the per-scene reviewing bracket below: that bracket
        // is empty for a media-only ask, and it lifts between batches of scenes, so it
        // can't say "the review phase is underway".
        announceCreationStage(
            this.dispatch,
            this.getState,
            CreationStage.REVIEWING,
            getVideoSizeAnalyticsProperties(this.luminary),
        );

        const mediaIds = args.media_ids ?? [];
        const sceneIds = args.scene_ids ?? [];

        if (mediaIds.length === 0 && sceneIds.length === 0) {
            return {
                result: 'Error: provide at least one media id (media_ids) or scene id (scene_ids) to ask about.',
                isSuccess: false,
            };
        }

        const resolvedScenes: ResolvedScene[] = [];
        for (const sceneId of sceneIds) {
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            if ('error' in resolved) {
                return { result: `Error: ${resolved.error}`, isSuccess: false };
            }
            resolvedScenes.push({ sceneId, sceneIndex: resolved.sceneIndex });
        }

        const reviewingSceneIds = resolvedScenes.map(
            ({ sceneIndex }) => this.luminary.scenes[sceneIndex].id,
        );
        if (reviewingSceneIds.length > 0) {
            this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: true }));
            // A reviewed scene is considered filled — clear its build-preview blur
            // right away (on its own, no need to wait for both text and media)
            // rather than after the vision call returns.
            this.recordSceneFillForScenes(
                resolvedScenes.map(({ sceneIndex }) => sceneIndex),
                'review',
            );
        }
        try {
            return await this.askAboutImages(args, context, mediaIds, resolvedScenes);
        } finally {
            if (reviewingSceneIds.length > 0) {
                this.dispatch(
                    setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: false }),
                );
            }
        }
    }

    private async askAboutImages(
        args: ImageAskArgs,
        context: ToolExecutionContext,
        mediaIds: string[],
        resolvedScenes: ResolvedScene[],
    ): Promise<EnhancedToolResult> {
        const { question } = args;
        const messageContent: (TextContent | ImageContent)[] = [];

        for (const id of mediaIds) {
            const resolved = this.resolveMediaImageUrl(id);
            if ('error' in resolved) {
                return { result: resolved.error, isSuccess: false };
            }
            messageContent.push(
                { type: 'text', text: `Media id ${id}:` },
                { type: 'image_url', image_url: { url: resolved.url } },
            );
        }

        for (const { sceneId, sceneIndex } of resolvedScenes) {
            // Re-checked each time round: rendering a scene is slow, so a skip clicked
            // during a multi-scene ask should drop the rest rather than finish the batch
            // and then ask the vision model about a review the user has already waved off.
            if (isReviewSkipped(this.getState())) {
                return { result: reviewSkippedResult(), isSuccess: true };
            }
            const rendered = await this.renderScene(sceneId, sceneIndex);
            if ('error' in rendered) {
                return { result: rendered.error, isSuccess: false };
            }
            messageContent.push(...rendered.content);
        }

        messageContent.push({
            type: 'text',
            text: `Answer the following question about the image(s) above as specifically and concisely as possible. Do not explain your reasoning unless asked.\n\nQuestion: ${question}`,
        });

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: `You are analyzing one or more images from a video. Answer the user's question about what you see.

You did not make this video and have no stake in it. Report what is actually in the frames, including problems the question did not anticipate. "Looks fine" is only an acceptable answer when you have checked each thing asked about and none of them is wrong.

Be specific and concise. When describing locations within an image, use terms like: top-left, top-center, top-right, center-left, center, center-right, bottom-left, bottom-center, bottom-right.`,
            tools: [],
            task: 'image_ask_tool',
            requiresVideoState: false,
        });

        conversation.addUserMessage(messageContent);

        try {
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
            });
            if (!result.success) {
                // A skip cancels the vision call, which surfaces here as a failure.
                if (isReviewSkipped(this.getState())) {
                    return { result: reviewSkippedResult(), isSuccess: true };
                }
                // A failed child conversation does not always carry a message; interpolating it
                // blind produced a literal "undefined" the agent could not act on.
                return {
                    result: `Error during image analysis: ${result.message || 'the vision model did not return a result. Try again in a moment.'}`,
                    isSuccess: false,
                };
            }
            return {
                isSuccess: true,
                result: result.message || 'No answer returned from image analysis.',
            };
        } catch (error) {
            if (isReviewSkipped(this.getState())) {
                return { result: reviewSkippedResult(), isSuccess: true };
            }
            return {
                result: `Error asking about images: ${error instanceof Error ? error.message : 'Unknown error'}`,
                isSuccess: false,
            };
        }
    }

    private resolveMediaImageUrl(id: string): ResolvedImage {
        const mediaRegistry = MediaRegistry.getInstance();
        const mediaData = mediaRegistry.getItem(id);
        if (!mediaData) {
            return { error: `Error: Media with ID "${id}" not found in registry.` };
        }

        const representation = mediaRegistry.getRepresentation(id);
        if (!representation) {
            return { error: `Error: Could not get representation for media ID "${id}".` };
        }

        // The "invisible" asset is a transparent placeholder used to show the
        // scene background - there's nothing meaningful to look at.
        if (
            mediaData.type === AssetContainerTypes.BACKGROUND &&
            mediaData.background?.type === SlideBackgroundModelTypeEnum.IMAGE &&
            mediaData.background?.image?.source === INVISIBLE_IMAGE_SOURCE_DATA.source &&
            mediaData.background?.image?.source_id === INVISIBLE_IMAGE_SOURCE_DATA.source_id
        ) {
            return {
                error: `Error: Media with ID "${id}" is an invisible/transparent image (effectively unset).`,
            };
        }

        if (representation.type === 'image' && mediaData.background?.image?.preview_url) {
            return { url: mediaData.background.image.preview_url };
        }
        if (representation.type === 'video' && mediaData.background?.video?.poster_url) {
            return { url: mediaData.background.video.poster_url };
        }
        const expectedField = representation.type === 'video' ? 'poster_url' : 'preview_url';
        return { error: `Error: Could not extract ${expectedField} from media ID "${id}".` };
    }

    private async renderScene(
        sceneId: string,
        sceneIndex: number,
    ): Promise<{ content: (TextContent | ImageContent)[] } | { error: string }> {
        if (sceneIndex < 0 || sceneIndex >= this.luminary.scenes.length) {
            return {
                error: `Error: scene index ${sceneIndex} is out of bounds. Video has ${this.luminary.scenes.length} scenes.`,
            };
        }
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return { error: `Error: No scene found at index ${sceneIndex}.` };
        }

        return this.renderSingleStill(sceneId, sceneIndex, SCREENSHOT_MAX_EDGE.high);
    }

    private async renderSingleStill(
        sceneId: string,
        sceneIndex: number,
        maxEdge: number,
    ): Promise<{ content: (TextContent | ImageContent)[] } | { error: string }> {
        const scene = this.luminary.scenes[sceneIndex];
        let imageData: string;
        try {
            imageData = await captureSceneScreenshot(this.luminary, scene, maxEdge);
        } catch {
            return {
                error: `Error: timed out rendering scene ${sceneDisplayId(this.luminary, scene.id)}. The video preview may be busy - try again in a moment.`,
            };
        }

        const content: (TextContent | ImageContent)[] = [
            { type: 'text', text: `Scene ${sceneId}:` },
        ];
        // A single still is an overview, not a spoken instant, so attach the whole scene's timed
        // narration (empty for a text-on-media scene; plain text when timings can't be mapped).
        const narration = formatSceneNarration(scene, this.getState());
        if (narration) {
            content.push({ type: 'text', text: narration });
        }
        content.push({ type: 'image_url', image_url: { url: imageData } });
        return { content };
    }
}
